import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Tabs,
  Tab,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetPlaybookQuery } from '@/services/api';
import Editor from '@monaco-editor/react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface PropertyRowProps {
  propertyKey: string;
  property: any;
  language: 'en' | 'ru';
  valueListLibrary?: any;
}


const PropertyRow: React.FC<PropertyRowProps> = ({ propertyKey, property, language, valueListLibrary }) => {
  // Helper function to render value list items for enum data types
  const renderValueListItems = () => {
    if (property.data_type === 'enum' && valueListLibrary) {
      // Get the value list reference - can be in values.list_id or values.value_list_ref
      const valueListRef = property.values?.list_id || property.values?.value_list_ref;
      
      if (valueListRef && valueListLibrary[valueListRef]) {
        const valueList = valueListLibrary[valueListRef];
        
        if (valueList && valueList.values && Array.isArray(valueList.values)) {
          // Handle array structure: [{id: "...", labels: {...}, synonyms: {...}}, ...]
          return (
            <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
              {valueList.values.map((valueData: any, index: number) => {
                const id = valueData.id || '';
                const label = valueData.labels?.[language] || valueData.labels?.en || id;
                const synonyms = valueData.synonyms?.[language] || [];
                const synonymsStr = synonyms.length > 0 ? ` (${synonyms.join(', ')})` : '';
                
                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>•</Typography>
                    <Chip
                      label={id}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ fontSize: '0.6rem', height: '16px', fontFamily: 'monospace' }}
                    />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      {label}
                      {synonyms.length > 0 && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', ml: 0.5 }}>
                          ({synonyms.join(', ')})
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        } else if (valueList && valueList.values && typeof valueList.values === 'object') {
          // Handle object structure: {key1: {labels: {...}, synonyms: {...}}, ...}
          return (
            <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
              {Object.entries(valueList.values).map(([valueId, valueData]: [string, any], index: number) => {
                const label = valueData.labels?.[language] || valueData.labels?.en || valueId;
                const synonyms = valueData.synonyms?.[language] || [];
                const synonymsStr = synonyms.length > 0 ? ` (${synonyms.join(', ')})` : '';
                
                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>•</Typography>
                    <Chip
                      label={valueId}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ fontSize: '0.6rem', height: '16px', fontFamily: 'monospace' }}
                    />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      {label}
                      {synonyms.length > 0 && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', ml: 0.5 }}>
                          ({synonyms.join(', ')})
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        }
      }
    }
    return null;
  };

  return (
    <TableRow hover>
      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
        {propertyKey}
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {property.preferred_name[language] || property.preferred_name.en || '-'}
        </Typography>
        {property.synonyms?.[language]?.length ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mt: 0.5 }}>
            {language === 'en' ? 'Synonyms: ' : 'Синонимы: '}{property.synonyms[language].join(', ')}
          </Typography>
        ) : null}
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontFamily: 'monospace' }}>
          {property.data_type}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ 
          backgroundColor: property.importance === 'high' ? 'error.light' : 
                          property.importance === 'medium' ? 'warning.light' : 'grey.300',
          color: property.importance === 'high' ? 'error.contrastText' : 
                 property.importance === 'medium' ? 'warning.contrastText' : 'text.primary',
          px: 1, 
          py: 0.5, 
          borderRadius: 1,
          display: 'inline-block',
          fontSize: '0.75rem'
        }}>
          {property.importance}
        </Typography>
      </TableCell>
      <TableCell>
        {property.data_type === 'enum' ? (
          renderValueListItems() || '-'
        ) : property.unit ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {property.unit.allowed && property.unit.allowed.length > 0 ? (
              <>
                <Typography variant="body2">
                  {property.unit.allowed.join(', ')}
                </Typography>
                <Typography variant="body2" color="text.secondary">→</Typography>
                <Chip
                  label={property.unit.normalize_to || property.unit.ucum}
                  size="small"
                  color="primary"
                  variant="filled"
                  sx={{ fontSize: '0.75rem', height: '20px' }}
                />
              </>
            ) : (
              <Chip
                label={property.unit.ucum}
                size="small"
                color="primary"
                variant="filled"
                sx={{ fontSize: '0.75rem', height: '20px' }}
              />
            )}
          </Box>
        ) : '-'}
      </TableCell>
    </TableRow>
  );
};


const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`playbook-tabpanel-${index}`}
      aria-labelledby={`playbook-tab-${index}`}
      style={{ width: '100%', maxWidth: 'none' }}
      {...other}
    >
      {value === index && <Box sx={{ py: 2, width: '100%', maxWidth: 'none' }}>{children}</Box>}
    </div>
  );
};

const PlaybookViewer: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const { language } = useSelector((state: RootState) => state.ui);

  const {
    data: playbook,
    error,
    isLoading,
  } = useGetPlaybookQuery(
    { category: categoryId! },
    { skip: !categoryId }
  );

  // Filter and group properties by aspects
  const groupedProperties = useMemo(() => {
    if (!playbook || !playbook.property_library) return [];

    const properties = Object.entries(playbook.property_library);
    
    // Filter properties by search term
    const filteredProperties = properties.filter(([key, property]) => {
      const searchLower = searchTerm.toLowerCase();
      
      const keyMatch = key.toLowerCase().includes(searchLower);
      const nameMatch = (property.preferred_name[language] || property.preferred_name.en || '').toLowerCase().includes(searchLower);
      const synonymsMatch = property.synonyms?.[language]?.some(synonym => 
        synonym.toLowerCase().includes(searchLower)
      ) || false;
      
      return keyMatch || nameMatch || synonymsMatch;
    });

    // Group properties by aspects and blocks
    const groups: Array<{
      aspectId: string;
      aspectName: string;
      aspectDescription?: string;
      blocks: Array<{
        blockId: string;
        blockName: string;
        properties: Array<[string, any]>;
      }>;
    }> = [];

    // Create a map of property refs to their data
    const propertyMap = new Map(filteredProperties);
    
    // Process each aspect
    (playbook.aspects || []).forEach(aspect => {
      const aspectGroup = {
        aspectId: aspect.id,
        aspectName: aspect.name[language] || aspect.name.en,
        aspectDescription: aspect.description?.[language] || aspect.description?.en,
        blocks: [] as Array<{
          blockId: string;
          blockName: string;
          properties: Array<[string, any]>;
        }>
      };

      // Process each block in the aspect
      aspect.blocks.forEach(block => {
        const blockProperties = block.properties
          .map(prop => [prop.ref, propertyMap.get(prop.ref)])
          .filter(([, property]) => property) as Array<[string, any]>;

        if (blockProperties.length > 0) {
          aspectGroup.blocks.push({
            blockId: block.id,
            blockName: block.name[language] || block.name.en,
            properties: blockProperties
          });
        }
      });

      if (aspectGroup.blocks.length > 0) {
        groups.push(aspectGroup);
      }
    });

    // Add ungrouped properties (not in any aspect/block)
    const groupedPropertyRefs = new Set();
    groups.forEach(group => 
      group.blocks.forEach(block => 
        block.properties.forEach(([ref]) => groupedPropertyRefs.add(ref))
      )
    );

    const ungroupedProperties = filteredProperties.filter(([key]) => !groupedPropertyRefs.has(key));
    
    if (ungroupedProperties.length > 0) {
      groups.push({
        aspectId: 'ungrouped',
        aspectName: language === 'en' ? 'Other Properties' : 'Прочие свойства',
        aspectDescription: language === 'en' ? 'Properties not organized into specific aspects' : 'Свойства, не организованные в конкретные аспекты',
        blocks: [{
          blockId: 'ungrouped-block',
          blockName: language === 'en' ? 'General' : 'Общие',
          properties: ungroupedProperties
        }]
      });
    }

    return groups;
  }, [playbook, searchTerm, language]);

  // Calculate total and filtered property counts
  const totalProperties = playbook ? Object.keys(playbook.property_library || {}).length : 0;
  const filteredCount = groupedProperties.reduce((sum, group) => 
    sum + group.blocks.reduce((blockSum, block) => blockSum + block.properties.length, 0), 0
  );


  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="40%" height={24} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  if (error) {
    const errorMessage = 'data' in error && error.data 
      ? (error.data as any).message || 'Failed to load playbook'
      : 'Failed to load playbook';

    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            {language === 'en' ? 'Error Loading Playbook' : 'Ошибка загрузки плейбука'}
          </Typography>
          <Typography variant="body2">
            {errorMessage}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (!playbook) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mt: 2 }}>
          {language === 'en' 
            ? `Playbook not found for category: ${categoryId}`
            : `Плейбук не найден для категории: ${categoryId}`}
        </Alert>
      </Box>
    );
  }

  const categoryName = categoryId?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';

  return (
    <Box sx={{ pt: 3, pl: 3, pb: 3, pr: 1, width: '100%', maxWidth: 'none', minWidth: '100%' }}>
      {/* Compact Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ mr: 'auto', minWidth: 0 }}>
          <Typography variant="h5" component="h1">
            {playbook.category.class.preferred_name?.[language] || 
             playbook.meta.title[language] || 
             playbook.meta.title.en || 
             categoryName}
          </Typography>
          {playbook.category.class.definition && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {playbook.category.class.definition[language] || playbook.category.class.definition.en}
            </Typography>
          )}
          {playbook.category.keywords && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {playbook.category.keywords[language]?.map((keyword, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  sx={{
                    px: 1,
                    py: 0.25,
                    bgcolor: 'grey.200',
                    borderRadius: 1,
                    color: 'text.secondary'
                  }}
                >
                  {keyword}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
        
        <Typography color="text.primary" variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
          {categoryId} / v{playbook.meta.version} ({new Date(playbook.meta.updated_at).toISOString().split('T')[0]})
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="playbook tabs"
            sx={{ width: '100%', maxWidth: 'none' }}
          >
            <Tab label={language === 'en' ? 'Properties' : 'Свойства'} />
            <Tab label={language === 'en' ? 'Guidance' : 'Руководство'} />
            <Tab label={language === 'en' ? 'Full JSON' : 'Полный JSON'} />
          </Tabs>
        </Box>

        {/* Full JSON Tab */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ 
            width: '100%',
            maxWidth: 'none'
          }}>
            <div style={{ width: '100%', display: 'block', position: 'relative' }}>
              <Editor
                width={`${Math.max(window.innerWidth - 320, 800)}px`}
                height="600px"
                defaultLanguage="json"
                value={JSON.stringify(playbook, null, 2)}
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  lineHeight: 19,
                  lineNumbers: 'on',
                  folding: true,
                  wordWrap: 'off',
                  automaticLayout: true,
                  padding: { top: 8, bottom: 8 },
                  scrollbar: {
                    vertical: 'auto',
                    horizontal: 'auto'
                  },
                  overviewRulerBorder: false,
                  hideCursorInOverviewRuler: true,
                  overviewRulerLanes: 0
                }}
              />
            </div>
          </Box>
        </TabPanel>

        {/* Properties Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ width: '100%' }}>
            {/* Search and Count Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={language === 'en' ? 'Search properties...' : 'Поиск свойств...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: 400 }}
              />
              <Box sx={{ ml: 'auto' }}>
                <Chip
                  label={searchTerm 
                    ? (language === 'en' ? `${filteredCount} of ${totalProperties} properties` : `${filteredCount} из ${totalProperties} свойств`)
                    : (language === 'en' ? `${totalProperties} properties` : `${totalProperties} свойств`)
                  }
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Box>

            {/* Grouped Properties */}
            {groupedProperties.length === 0 ? (
              <Alert severity="info">
                {searchTerm 
                  ? (language === 'en' ? 'No properties match your search.' : 'Свойства не найдены по вашему запросу.')
                  : (language === 'en' ? 'No properties found.' : 'Свойства не найдены.')
                }
              </Alert>
            ) : (
              groupedProperties.map((group) => (
                <Accordion key={group.aspectId} defaultExpanded sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="h6">
                        {group.aspectName}
                      </Typography>
                      {group.aspectDescription && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {group.aspectDescription}
                        </Typography>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {group.blocks.map((block) => (
                      <Box key={block.blockId} sx={{ mb: block !== group.blocks[group.blocks.length - 1] ? 3 : 0 }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                          {block.blockName}
                          <Chip 
                            label={block.properties.length} 
                            size="small" 
                            sx={{ ml: 1 }}
                            variant="outlined"
                          />
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ width: '100%' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>{language === 'en' ? 'Property Key' : 'Ключ свойства'}</strong></TableCell>
                                <TableCell><strong>{language === 'en' ? 'Name' : 'Название'}</strong></TableCell>
                                <TableCell><strong>{language === 'en' ? 'Data Type' : 'Тип данных'}</strong></TableCell>
                                <TableCell><strong>{language === 'en' ? 'Importance' : 'Важность'}</strong></TableCell>
                                <TableCell><strong>{language === 'en' ? 'Unit' : 'Единица'}</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {block.properties.map(([key, property]) => (
                                <PropertyRow
                                  key={key}
                                  propertyKey={key}
                                  property={property}
                                  language={language}
                                  valueListLibrary={playbook?.value_list_library}
                                />
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        </TabPanel>


        {/* Guidance Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ width: '100%', p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {language === 'en' ? 'Parsing Guidance' : 'Руководство по парсингу'}
            </Typography>
            {playbook.parsing_guidance[language]?.map((guidance, index) => (
              <Typography key={index} variant="body2" paragraph>
                • {guidance}
              </Typography>
            )) || playbook.parsing_guidance.en?.map((guidance, index) => (
              <Typography key={index} variant="body2" paragraph>
                • {guidance}
              </Typography>
            ))}
          </Box>
        </TabPanel>
      </Box>
    </Box>
  );
};

export default PlaybookViewer;